import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useChecklistListaPagination } from "../hooks/Checklist_lista/pagination";
import { useChecklistListaFilters } from "../hooks/Checklist_lista/filter";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ChecklistListaForm, ChecklistListaSchema } from "../schemas/Checklist_lista/Checklist_lista";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { ChecklistLista } from "../../domain/models/checklist_Lista";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";




type LoadProjetoFuncState = {
  Nome?: string | null;
  Status?: number | null;
  Page?: number | null;
  RowsPerPage?: number | null;
};

export const useCheckListViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    projetos: {
      getAllProjetosChecklistUseCase,
      createProjetosChecklistUseCase,
      updateProjetosChecklistUseCase,
      deleteProjetosChecklistUseCase,
    },
  } = useDependencyInjection();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  

  const pagination = useChecklistListaPagination();
  const filters = useChecklistListaFilters();

  const hookForm = useForm<ChecklistListaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ChecklistListaSchema),
  });
  
  const { pushErrorNotification, pushInfoNotification} = usePageLoader();

  const [checklistLista, setChecklistLista] = useState<ChecklistLista[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistLista | undefined> (undefined);
  const [loading, setLoading] = useState(false);
  const debouncedRef = useCreateDebounceHandler(750);
  const [checklistModal, setChecklistModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const backOrNavigate = useBackOrNavigate();
  


  //#region Funções de carregamento
  const loadChecklistLista = useCallback(
    async (funcState?: LoadProjetoFuncState) => {
      try {
        setLoading(true);
        const filterNome = funcState?.Nome ?? filters.nome;
        const filterStatus = funcState?.Status ?? filters.status;
        const filterpage = funcState?.Page ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;

        await getAllProjetosChecklistUseCase.getAllProjetosChecklist(filterpage, filterRowspage, filterNome, filterStatus);

        setChecklistLista(getAllProjetosChecklistUseCase.items);
        pagination.setRowsPerPage(getAllProjetosChecklistUseCase.pageSize);
        pagination.setTotalItems(getAllProjetosChecklistUseCase.totalItems);
        setLoading(false);
      } catch (e: any) {
        pushErrorNotification("Atenção", "Não é possível excluir este Checklist, pois ele já está vinculado a uma ou mais Disciplinas.");
      } finally {
        setLoading(false);
      }
    },
    [filters.nome, filters.status, pagination, getAllProjetosChecklistUseCase, pushErrorNotification],
  );

  //#endregion


  //#region useEffect
  usePageStartBootstrap(async () => {
    try {
      await loadChecklistLista();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });
  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadChecklistLista();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadChecklistLista, pushErrorNotification]);

  //#endregion
  
  
  //#region Filtros e paginação
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useChecklistListaPagination.setState({ page: page + 1 });
        await loadChecklistLista({ Page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [loadChecklistLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadChecklistLista({ Page: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [loadChecklistLista, pagination, pushErrorNotification],
  );

  const handleNomeChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string) => {
      useChecklistListaFilters.getState().setNome(newValue);
      return async () => {
        try {
          setLoading(true);
          useChecklistListaPagination.getState().setPage(1);
          await loadChecklistLista({Nome: newValue, Page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, loadChecklistLista],
  );
  const handleStatusoChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
      useChecklistListaFilters.getState().setStatus(newValue);
      return async () => {
        try {
          setLoading(true);
          useChecklistListaPagination.getState().setPage(1);
          await loadChecklistLista({ Status: newValue, Page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
        } finally {
          setLoading(false);
        }
      }
    }, [ loadChecklistLista, pushErrorNotification]);

  const handleClearFilters = useCallback(async () => {
    try {
        setLoading(true);
        filters.clearFilters();
        pagination.setPage(1);
        await loadChecklistLista({
          Nome: "",
          Page: 1
        });
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar Diário de Obras!'));
    } finally {
        setLoading(false);
    }
  }, [filters, loadChecklistLista, pagination, pushErrorNotification]);

  //#endregion
  
  //#region Evento

  const handleModalOpenState = useCallback((idChecklist: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklist && checklistLista) {
        setSelectedChecklist(checklistLista.find(x => x.IdChecklist === idChecklist));
      } else {
        setSelectedChecklist(undefined);
      }
    }
    setChecklistModal(openState);
  }, [checklistLista]);

  const handleCheckListSubmit = useCallback(
    async (form: ChecklistListaForm) => {
      try {
        setSaving(true);
        if (selectedChecklist) {
          await updateProjetosChecklistUseCase.updateProjetosChecklist(form.idChecklist ?? 0, form.statusCheckList, form.nome ?? "", form.descricao ?? undefined);
          pushInfoNotification("SUCESSO", "CheckLis atualizado com sucesso!!", { isAutoFinish: true })
        } else {
          await createProjetosChecklistUseCase.createProjetosChecklist(form.statusCheckList, form.nome ?? "", form.descricao ?? undefined);
          pushInfoNotification("SUCESSO", "CheckLis criado com sucesso!!", { isAutoFinish: true })
        }
        await loadChecklistLista();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar Checklist!"));
      } finally {
        setSaving(false);
        setChecklistModal(false);
      }
    },
    [selectedChecklist, loadChecklistLista, updateProjetosChecklistUseCase, pushInfoNotification, createProjetosChecklistUseCase, pushErrorNotification],
  );

  const DeletarCheckList = useCallback(async () => {
    if (selectedChecklist) {
      try {
          setSaving(true);
          await deleteProjetosChecklistUseCase.deleteProjetosChecklist(selectedChecklist.IdChecklist);
          await loadChecklistLista();
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
          setSaving(false);
          setOpenDeleteModal(false);
      }
    }
  }, [deleteProjetosChecklistUseCase, loadChecklistLista, pushErrorNotification, selectedChecklist]);


  const DeleteOpenModal = useCallback((id: number | null, openState: boolean) => {
    if (openState) {
      if (id && checklistLista) {
        setSelectedChecklist(checklistLista.find(x => x.IdChecklist === id));
      } else {
        setSelectedChecklist(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [checklistLista]);

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } 
    },
    [ changeHistory],
  );
    

  //#endregion



  const handleViewChecklist = useCallback(
    (idChecklist: number | null, openNewTab: boolean) => {
      if (openNewTab) {
        window.open(`/ChecklistItens_cadastro/${idChecklist}`, "_blank", "noopener,noreferrer");
      } else {
        backOrNavigate(`/ChecklistItens_cadastro/${idChecklist}`);
      }
    },
    [backOrNavigate],
  );

  return {
    loading,
    errorMessage,
    pagination,
    filters,
    checklistLista,
    checklistModal,
    hookForm,
    saving,
    openDeleteModal,
    selectedChecklist,
    changeHistory,
    handleOpenChangeHistory,
    setChecklistModal,
    handlePageChange,
    handleRowsPerPageChange,
    handleNomeChange,
    handleClearFilters,
    handleModalOpenState,
    handleCheckListSubmit,
    DeleteOpenModal,
    DeletarCheckList,
    handleViewChecklist,
    handleReload,
    handleStatusoChange,
  };
};
